import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, KeyRound } from "lucide-react";
import { Reveal, Kicker } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";

const LAYERS = [
  {
    n: "Layer 01",
    icon: ShieldCheck,
    chip: "Patent-pending",
    title: "Patent coverage",
    body: "Patent filings cover the formulated product and its method of use in products intended for canine methylxanthine toxicosis. Filings are pursued in key veterinary markets; status and claim scope are shared with qualified partners under NDA.",
  },
  {
    n: "Layer 02",
    icon: KeyRound,
    chip: "Never licensed · supplied only",
    title: "Trade-secret process",
    body: "The proprietary process behind the active is held as a trade secret. It is not licensed, published or disclosed · the active is only ever supplied by Chocolate Detox. This layer is the basis of our sole-source position.",
  },
];

export default function IntellectualProperty() {
  return (
    <div data-testid="ip-page">
      <section className="hero-grid pt-32 lg:pt-40 pb-16 lg:pb-20 border-b border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal y={16}>
            <Kicker index="§3">Intellectual Property</Kicker>
            <h1 className="mt-6 font-headline font-semibold tracking-tight text-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.06] max-w-4xl">
              Two layers. One source.
            </h1>
            <p className="mt-7 text-base sm:text-lg text-smoke leading-relaxed max-w-3xl">
              Our position combines patent coverage over the formulated product and its method of
              use with a proprietary process held as a trade secret · making Chocolate Detox the
              sole source of the active.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-24" data-testid="ip-layers">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-px bg-line border border-line">
          {LAYERS.map((l, i) => (
            <Reveal key={l.n} delay={i * 0.1} className="bg-white p-8 lg:p-12 hover:bg-pinelight/30 transition-colors duration-300">
              <div className="flex items-center justify-between">
                <p className="font-mono text-xs text-pine">{l.n}</p>
                <span className="inline-flex items-center px-2.5 py-1 font-mono text-[10px] font-medium uppercase tracking-[0.15em] bg-pinelight text-pine border border-pine/20">
                  {l.chip}
                </span>
              </div>
              <l.icon className="mt-8 text-pine" size={28} strokeWidth={1.5} />
              <h2 className="mt-5 font-headline font-semibold tracking-tight text-ink text-2xl lg:text-3xl">{l.title}</h2>
              <p className="mt-4 text-sm sm:text-base text-smoke leading-relaxed">{l.body}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-obsidian text-white py-20 lg:py-24" data-testid="ip-territory">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.22em] text-pinelight/70">Territory model</p>
              <h2 className="mt-5 font-headline font-semibold tracking-tight text-2xl sm:text-3xl lg:text-4xl leading-tight">
                Exclusivity, granted selectively.
              </h2>
              <p className="mt-5 text-white/60 text-base leading-relaxed max-w-xl">
                Territory licences are granted to qualified manufacturers and distributors on an
                exclusive or first-look basis. The active itself is supplied, never licensed,
                preserving a single global specification.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.12}>
              <div className="border border-white/10 divide-y divide-white/10 font-mono text-xs uppercase tracking-[0.18em]">
                <p className="px-6 py-4 text-white/50">Formulated product · patented</p>
                <p className="px-6 py-4 text-white/50">Method of use · patented</p>
                <p className="px-6 py-4 text-pinelight">Process · trade secret, supplied only</p>
                <p className="px-6 py-4 text-white/50">Territories · licensed selectively</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee />

      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
          <Reveal>
            <h2 className="font-headline font-semibold tracking-tight text-ink text-2xl sm:text-3xl max-w-xl leading-tight">
              Discuss patent status and territory availability for your market.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/partnering"
              data-testid="ip-partnering-cta"
              className="inline-flex items-center gap-2 bg-pine hover:bg-pinehover text-white text-sm font-medium px-7 py-4 transition-colors duration-200 shrink-0"
            >
              Partnership enquiries <ArrowUpRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
